import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { getLocale } from '@/helpers/dates';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

const InputDateForm = ({
  name,
  placeholder,
  className,
  label,
  isRequired = false,
  description,
  isDisabled,
  minDate,
  maxDate,
  dateText = false,
  cleanIfDisable = false,
  onDaySelect = () => {},
  validateFunction, // Función de validación personalizada
  triggerOnSelect = false,
  inputClassName = '',
  captionLayout = 'label',
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext();
  const { t } = useTranslation('translation', { keyPrefix: 'inputs' });
  const language = useSelector((state) => state.app.language);
  const locale = getLocale(language);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleDaySelect = (date) => {
    if (
      date &&
      (selectedDate ? format(date, 'y-MM-dd') !== format(selectedDate, 'y-MM-dd') : true)
    ) {
      setSelectedDate(date);
      const formattedDate = format(date, 'y-MM-dd');
      setValue(name, formattedDate);
      if (triggerOnSelect) trigger(name); // Disparar validación después de cambiar el valor
    } else {
      setSelectedDate(null);
      setValue(name, '');
    }
    onDaySelect();
  };

  const getDate = (value) => {
    if (locale.code === 'es') {
      return format(new Date(value), 'dd MMMM yyyy', { locale });
    } else return format(new Date(value), 'MMMM dd, yyyy', { locale });
  };

  const getDateTextFormatted = (value) => {
    if (locale.code === 'es') {
      return format(new Date(value), "dd 'de' MMMM 'de' yyyy", { locale });
    } else return format(new Date(value), 'MMMM dd, yyyy', { locale });
  };

  useEffect(() => {
    if (isDisabled && cleanIfDisable) {
      setValue(name, '');
    }
  }, [isDisabled]);

  return (
    <div className={`${className}`}>
      <FormField
        control={control}
        name={name}
        defaultValue=''
        rules={{
          required: isRequired ? t('required') : false,
          validate: validateFunction ? validateFunction : {},
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={'outline'}
                    className={cn(
                      ' tw-pl-3 tw-text-left tw-font-normal d-flex tw-w-full',
                      !field.value && 'tw-text-muted-foreground',
                      errors[name] && '!tw-border-red-600',
                    )}
                  >
                    {field.value ? (
                      dateText ? (
                        <span className={inputClassName}>
                          {getDateTextFormatted(selectedDate ? selectedDate : field.value)}
                        </span>
                      ) : (
                        <span className={inputClassName}>
                          {getDate(selectedDate ? selectedDate : field.value)}
                        </span>
                      )
                    ) : (
                      placeholder
                    )}

                    <CalendarIcon className='tw-ml-auto tw-h-4 tw-w-4 tw-opacity-50' />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className='tw-w-auto tw-p-0 p-0' align='start'>
                <Calendar
                  mode='single'
                  selected={
                    field?.value?.includes('T')
                      ? new Date(field.value)
                      : new Date(field.value + 'T00:00:00')
                  }
                  locale={locale}
                  onSelect={(date) => handleDaySelect(date)}
                  date={selectedDate ? selectedDate : field.value}
                  disabled={[{ before: minDate, after: maxDate }]}
                  captionLayout={captionLayout}
                />
              </PopoverContent>
            </Popover>
            {description ? <FormDescription>{description}</FormDescription> : null}
            {errors[name] && (
              <div className='d-flex align-items-center error-content'>
                <i className='fas fa-exclamation-triangle text-danger mr-2'></i>
                <p className='text-danger p-0 m-0'>{errors[name].message}</p>
              </div>
            )}
          </FormItem>
        )}
      />
    </div>
  );
};

export default InputDateForm;
